/**
 * Customer detail: the customer's portal rollout invitations (tracker item 81).
 * Read-only history from GET /portal-rollout/customers/:id ("invited on ...,
 * not activated yet" and one line per campaign). Inviting stays in the Customer
 * portal panel above; this panel only shows where the customer stands in the
 * company's rollout campaigns. ADMIN and OFFICE_MANAGER.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { createAPIClient, IPortalRolloutApi } from '../api';
import * as types from '../api/types';
import { getErrorMessage } from '../utils/apiError';
import {
  CAMPAIGN_STATUS_LABELS,
  RECIPIENT_STATUS_CLASSES,
  RECIPIENT_STATUS_LABELS,
  dateTimeText,
  plural,
  rolloutCampaignPath,
} from '../pages/portalRollout/portalRolloutUi';

export function CustomerRolloutPanel({ customerId }: { customerId: string }) {
  const api = useMemo<IPortalRolloutApi>(() => createAPIClient(), []);
  const [data, setData] = useState<types.CustomerRollout | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setError(null);
    try {
      setData(await api.getCustomerPortalRollout(customerId));
    } catch (cause) {
      setError(getErrorMessage(cause, 'Could not load the portal rollout history'));
    }
  }, [api, customerId]);

  useEffect(() => { void load(); }, [load]);

  const summary = data
    ? data.status === null
      ? 'Not part of a portal rollout campaign.'
      : data.status === 'ACTIVATED'
        ? 'Accepted the invitation and set a password.'
        : data.lastInvitedAt
          ? `Invited ${dateTimeText(data.lastInvitedAt)}; not activated yet.`
          : 'In a rollout campaign; no invitation sent yet.'
    : null;

  return (
    <section aria-labelledby="customer-rollout-heading" className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 sm:p-6" data-testid="customer-rollout-panel">
      <h2 id="customer-rollout-heading" className="text-lg font-semibold text-gray-900">Portal rollout</h2>
      {!data && !error && <p role="status" className="mt-2 text-sm text-gray-600">Loading…</p>}
      {error && <p role="alert" className="mt-2 rounded bg-red-50 p-3 text-sm text-red-800">{error} <button type="button" className="underline" onClick={() => void load()}>Try again</button></p>}
      {data && (
        <>
          <p className="mt-1 text-sm text-gray-600" data-testid="rollout-summary">{summary}</p>
          {data.invitations.length > 0 && (
            <ul className="mt-3 divide-y divide-gray-100" aria-label="Rollout invitations">
              {data.invitations.map((invitation) => (
                <li key={invitation.recipientId} className="py-2 text-sm" data-testid="rollout-invitation">
                  <div className="flex flex-wrap items-start justify-between gap-2">
                    <Link to={rolloutCampaignPath(invitation.campaignId)} className="min-w-0 break-words font-medium text-blue-700 underline">{invitation.campaignName}</Link>
                    <span className={`rounded px-2 py-0.5 text-xs font-semibold ${RECIPIENT_STATUS_CLASSES[invitation.status]}`}>{RECIPIENT_STATUS_LABELS[invitation.status]}</span>
                  </div>
                  <p className="text-xs text-gray-500">
                    Campaign {CAMPAIGN_STATUS_LABELS[invitation.campaignStatus].toLowerCase()}.
                    {invitation.sendCount > 0 ? ` Sent ${plural(invitation.sendCount, 'time')}, last ${dateTimeText(invitation.lastSentAt)}.` : ''}
                    {invitation.activatedAt ? ` Activated ${dateTimeText(invitation.activatedAt)}.` : ''}
                  </p>
                </li>
              ))}
            </ul>
          )}
        </>
      )}
    </section>
  );
}
